import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Check, ChevronDown, Copy, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, apiReady, formatApiError, humanPause, newId } from "../../lib/api";
import { scrollToTarget } from "../layout/SmoothScroll";
import { Logo } from "../brand/Logo";
import { enquiryOptions, enquiryTypes } from "../../content/site";
import { services } from "../../content/services";

const empty = { name: "", email: "", phone: "", organisation: "", location: "", industry: "", project_timeline: "", budget_range: "", preferred_contact: "", message: "", services: [], consent: false, website: "" };
const DETAIL_FIELDS = [
  ["industry", "Industry", "f-industry"], ["project_timeline", "Expected timeline", "f-timeline"],
  ["budget_range", "Budget range", "f-budget"], ["preferred_contact", "Preferred contact method", "f-contact-pref"],
];
const emailOk = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v);

const Field = ({ label, id, error, children, optional }) => (
  <label htmlFor={id} className="block">
    <span className="flex items-baseline justify-between font-mono text-[11px] uppercase tracking-[0.2em] text-stone-600">
      {label} {optional && <span className="normal-case tracking-normal text-stone-500">optional</span>}
    </span>
    {children}
    {error && <span className="mt-1.5 block text-xs text-red-700" role="alert">{error}</span>}
  </label>
);

const inputCls = "mt-2 h-12 w-full rounded-xl border border-stone-300 bg-white px-4 text-[15px] text-stone-900 outline-none transition-[border-color,box-shadow] duration-200 placeholder:text-stone-400 focus:border-orange-600 focus:ring-4 focus:ring-orange-600/15 aria-[invalid=true]:border-red-600";

const Success = ({ reference, category, ack, email, token, onReset }) => {
  const trackPath = `/track?ref=${encodeURIComponent(reference)}#t=${encodeURIComponent(token ?? "")}`;
  return (
  <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="rounded-[24px] border border-stone-300 bg-white p-8 sm:p-10" data-testid="enquiry-success">
    <span className="flex items-start justify-between gap-4">
      <span className="grid h-12 w-12 place-items-center rounded-full bg-orange-600 text-white"><Check className="h-6 w-6" /></span>
      <Logo variant="light" className="h-14 w-auto" />
    </span>
    <p className="mt-6 font-display text-2xl text-stone-900 sm:text-3xl">Thank you — we've received your request.</p>
    <p className="mt-3 text-stone-600">Please keep your reference number — quote it in any follow-up so we can find your request quickly.</p>
    <p className="mt-2 text-sm text-stone-500" data-testid="enquiry-category">Category: <span className="font-medium text-stone-800">{category}</span></p>
    {ack === "queued" && <p className="mt-2 text-sm text-stone-600" data-testid="enquiry-ack-note">A confirmation email is on its way to <span className="font-medium text-stone-800">{email}</span>.</p>}
    <div className="mt-8 flex flex-wrap items-center gap-3">
      <span className="rounded-xl bg-stone-900 px-5 py-3 font-mono text-lg tracking-wider text-orange-400" data-testid="enquiry-reference">{reference}</span>
      <button type="button" onClick={() => { navigator.clipboard?.writeText(reference); toast.success("Reference copied"); }}
        className="inline-flex h-12 items-center gap-2 rounded-xl border border-stone-300 px-4 text-sm text-stone-700 hover:border-stone-900" data-testid="enquiry-copy-ref">
        <Copy className="h-4 w-4" /> Copy
      </button>
    </div>
    {token && (
      <div className="mt-8 rounded-2xl border border-orange-200 bg-orange-50/60 p-5" data-testid="enquiry-tracking">
        <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-orange-800">Private tracking link</p>
        <p className="mt-2 text-sm text-stone-700">Save this link to check progress at any time. Keep it private — together with your reference it opens your status page.</p>
        <div className="mt-4 flex flex-col gap-2 sm:flex-row">
          <Link to={trackPath} data-testid="enquiry-track-link" className="inline-flex h-11 items-center justify-center rounded-full bg-stone-900 px-5 text-sm font-semibold text-white hover:bg-orange-700">Track my request</Link>
          <button type="button" data-testid="enquiry-copy-track" onClick={() => { navigator.clipboard?.writeText(`${window.location.origin}${trackPath}`); toast.success("Tracking link copied"); }}
            className="inline-flex h-11 items-center justify-center gap-2 rounded-full border border-stone-300 px-5 text-sm text-stone-700 hover:border-stone-900"><Copy className="h-4 w-4" /> Copy link</button>
        </div>
      </div>
    )}
    <div className="mt-10 border-t border-stone-200 pt-8" data-testid="enquiry-next-steps">
      <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-stone-500">What happens next</p>
      <ol className="mt-4 space-y-3 text-sm text-stone-700">
        {["Your request is securely logged and visible only to authorised TechCare staff.", "A team member reviews the details and may contact you to clarify scope.", "We respond with next steps — a proposal, a survey appointment or an answer to your question."].map((t, i) => (
          <li key={t} className="flex gap-3"><span className="font-mono text-xs text-orange-700">0{i + 1}</span>{t}</li>
        ))}
      </ol>
    </div>
    <button type="button" onClick={onReset} className="tc-link mt-8 text-sm font-semibold text-orange-700" data-testid="enquiry-new-btn">Send another enquiry</button>
  </motion.div>
  );
};

export const EnquiryForm = ({ defaultType = "quote", defaultService }) => {
  const [params] = useSearchParams();
  const initialType = enquiryTypes.some((t) => t.id === params.get("type")) ? params.get("type") : defaultType;
  const [type, setType] = useState(initialType);
  const refParam = /^TC-[A-Z0-9-]{4,30}$/i.test(params.get("ref") ?? "") ? params.get("ref").toUpperCase() : "";
  const [form, setForm] = useState({ ...empty, message: refParam ? `Regarding ${refParam}: ` : "", services: defaultService ? [defaultService] : [] });
  const [errors, setErrors] = useState({});
  const [showDetails, setShowDetails] = useState(false);
  const [busy, setBusy] = useState(false);
  const [serverError, setServerError] = useState("");
  const [reference, setReference] = useState(null);
  const [result, setResult] = useState({});
  const started = useRef(Date.now());
  const submissionId = useRef(newId());

  useEffect(() => {
    const t = params.get("type");
    if (enquiryTypes.some((x) => x.id === t)) setType(t);
  }, [params]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));
  const toggleService = (name) =>
    setForm((f) => ({ ...f, services: f.services.includes(name) ? f.services.filter((s) => s !== name) : [...f.services, name] }));

  const validate = () => {
    const e = {};
    if (form.name.trim().length < 2) e.name = "Please enter your name.";
    if (!emailOk(form.email.trim())) e.email = "Please enter a valid email address.";
    if (form.phone && !/^[0-9+()\-\s]{6,40}$/.test(form.phone)) e.phone = "Use digits, spaces, + ( ) or - only.";
    if (form.message.trim().length < 10) e.message = "Please tell us a little more (at least 10 characters).";
    if (type === "site_survey" && !form.location.trim()) e.location = "Please tell us where the site is.";
    if (["Phone call", "WhatsApp"].includes(form.preferred_contact) && !form.phone.trim()) e.phone = "Add a phone number so we can contact you that way.";
    if (!form.consent) e.consent = "Please confirm we may contact you.";
    setErrors(e);
    if (Object.keys(e).length) setTimeout(() => document.querySelector("[data-testid='enquiry-form'] [aria-invalid='true']")?.focus(), 30);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setServerError("");
    if (!apiReady) { setServerError("Enquiries are not connected on this deployment yet."); return; }
    setBusy(true);
    try {
      await humanPause(started.current);
      const { data } = await api.post("/enquiries", {
        ...form, type, phone: form.phone || null, organisation: form.organisation || null, location: form.location || null,
        industry: form.industry || null, project_timeline: form.project_timeline || null, budget_range: form.budget_range || null, preferred_contact: form.preferred_contact || null,
        elapsed_ms: Date.now() - started.current, source_page: window.location.pathname, submission_id: submissionId.current,
      });
      setReference(data?.reference ?? "");
      setResult({ ack: data?.acknowledgement ?? "disabled", token: data?.tracking_token ?? null, email: form.email.trim(), category: enquiryTypes.find((t) => t.id === type)?.label ?? "Enquiry" });
      setTimeout(() => scrollToTarget("[data-testid='enquiry-success']"), 80);
    } catch (err) {
      setServerError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  if (reference) return <Success reference={reference} {...result} onReset={() => { setReference(null); setForm(empty); started.current = Date.now(); submissionId.current = newId(); }} />;

  return (
    <form onSubmit={submit} noValidate className="rounded-[24px] border border-stone-300 bg-white p-6 sm:p-10" data-testid="enquiry-form">
      <div role="radiogroup" aria-label="Enquiry type" className="flex flex-wrap gap-2">
        {enquiryTypes.map((t) => (
          <button key={t.id} type="button" role="radio" aria-checked={type === t.id} onClick={() => setType(t.id)} data-testid={`enquiry-type-${t.id}`}
            className={`rounded-full border px-4 py-2 text-sm transition-[background-color,border-color,color] duration-200 ${type === t.id ? "border-stone-900 bg-stone-900 text-white" : "border-stone-300 text-stone-700 hover:border-stone-900"}`}>
            {t.label}
          </button>
        ))}
      </div>
      <p className="mt-4 text-sm leading-relaxed text-stone-600" data-testid="enquiry-type-hint">{enquiryTypes.find((t) => t.id === type)?.hint}</p>

      <div className="mt-8 grid gap-5 sm:grid-cols-2">
        <Field label="Full name" id="f-name" error={errors.name}>
          <input id="f-name" className={inputCls} value={form.name} onChange={set("name")} autoComplete="name" maxLength={120} aria-invalid={!!errors.name} data-testid="enquiry-name-input" />
        </Field>
        <Field label="Email" id="f-email" error={errors.email}>
          <input id="f-email" type="email" inputMode="email" autoCapitalize="none" className={inputCls} value={form.email} onChange={set("email")} autoComplete="email" maxLength={200} aria-invalid={!!errors.email} data-testid="enquiry-email-input" />
        </Field>
        <Field label="Phone" id="f-phone" error={errors.phone} optional>
          <input id="f-phone" type="tel" inputMode="tel" placeholder="+254 …" className={inputCls} value={form.phone} onChange={set("phone")} autoComplete="tel" maxLength={40} aria-invalid={!!errors.phone} data-testid="enquiry-phone-input" />
        </Field>
        <Field label="Organisation" id="f-org" optional>
          <input id="f-org" className={inputCls} value={form.organisation} onChange={set("organisation")} autoComplete="organization" maxLength={160} data-testid="enquiry-org-input" />
        </Field>
        <div className="sm:col-span-2">
          <Field label={type === "site_survey" ? "Site location" : "Project location"} id="f-loc" error={errors.location} optional={type !== "site_survey"}>
            <input id="f-loc" className={inputCls} value={form.location} onChange={set("location")} placeholder="Town / area, Kenya" maxLength={160} aria-invalid={!!errors.location} data-testid="enquiry-location-input" />
          </Field>
        </div>
      </div>

      <div className="mt-7 rounded-2xl border border-stone-200 bg-stone-50/70" data-testid="enquiry-details-panel">
        <button type="button" aria-expanded={showDetails} aria-controls="enquiry-details" onClick={() => setShowDetails((v) => !v)} data-testid="enquiry-details-toggle"
          className="flex w-full items-center justify-between gap-4 rounded-2xl px-5 py-4 text-left outline-none focus-visible:ring-4 focus-visible:ring-orange-600/15">
          <span>
            <span className="block text-sm font-semibold text-stone-900">Organisation &amp; project details</span>
            <span className="mt-0.5 block text-xs text-stone-600">Optional — industry, timeline, budget and how you'd like us to reach you.</span>
          </span>
          <ChevronDown className={`h-5 w-5 shrink-0 text-stone-600 transition-transform duration-300 ${showDetails ? "rotate-180" : ""}`} />
        </button>
        {showDetails && (
          <div id="enquiry-details" className="grid gap-5 border-t border-stone-200 px-5 pb-5 pt-4 sm:grid-cols-2">
            {DETAIL_FIELDS.map(([k, label, id]) => (
              <Field key={k} label={label} id={id} optional>
                <span className="relative block">
                  <select id={id} className={`${inputCls} appearance-none pr-10`} value={form[k]} onChange={set(k)} data-testid={`enquiry-${k.replaceAll("_", "-")}-select`}>
                    <option value="">Select…</option>
                    {enquiryOptions[k].map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                  <ChevronDown aria-hidden="true" className="pointer-events-none absolute right-4 top-1/2 mt-1 h-4 w-4 -translate-y-1/2 text-stone-500" />
                </span>
              </Field>
            ))}
          </div>
        )}
      </div>

      <fieldset className="mt-7">
        <legend className="font-mono text-[11px] uppercase tracking-[0.2em] text-stone-600">Services of interest <span className="normal-case tracking-normal text-stone-500">optional</span></legend>
        <div className="mt-3 flex flex-wrap gap-2">
          {services.map((s) => {
            const on = form.services.includes(s.name);
            return (
              <button key={s.slug} type="button" aria-pressed={on} onClick={() => toggleService(s.name)} data-testid={`enquiry-service-${s.slug}`}
                className={`rounded-full border px-3.5 py-1.5 text-[13px] transition-[background-color,border-color,color] duration-200 ${on ? "border-orange-600 bg-orange-600 text-white" : "border-stone-300 text-stone-700 hover:border-orange-600"}`}>
                {s.name}
              </button>
            );
          })}
        </div>
      </fieldset>

      <div className="mt-7">
        <Field label={type === "contact" ? "How can we help?" : "Project description"} id="f-msg" error={errors.message}>
          <textarea id="f-msg" rows={5} placeholder="What do you need, where, and roughly when?" className={`${inputCls} h-auto py-3`} value={form.message} onChange={set("message")} maxLength={4000} aria-invalid={!!errors.message} data-testid="enquiry-message-input" />
        </Field>
      </div>

      <div aria-hidden="true" className="absolute -left-[9999px] h-px w-px overflow-hidden">
        <label>Website<input tabIndex={-1} autoComplete="off" value={form.website} onChange={set("website")} /></label>
      </div>

      <label className="mt-6 flex items-start gap-3 text-sm text-stone-700">
        <input type="checkbox" checked={form.consent} onChange={set("consent")} aria-invalid={!!errors.consent} className="mt-0.5 h-5 w-5 shrink-0 accent-orange-600" data-testid="enquiry-consent-checkbox" />
        <span>I agree that TechCare Technologies may store these details and contact me about this enquiry.</span>
      </label>
      {errors.consent && <p className="mt-1.5 text-xs text-red-700" role="alert">{errors.consent}</p>}
      <p className="mt-3 text-xs leading-relaxed text-stone-600" data-testid="enquiry-privacy-notice">
        Privacy notice: we use these details only to respond to your request and keep you updated on it. We don't sell them or use them for advertising. Please don't include passwords, payment or M-Pesa details, or ID numbers. To ask about, correct or delete your data, email hello@techcare.live. Our full privacy policy will be published soon.
      </p>
      {serverError && (
        <p className="mt-6 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800" role="alert" data-testid="enquiry-server-error">{serverError}</p>
      )}

      <button type="submit" disabled={busy} data-testid="enquiry-submit-btn"
        className="group mt-8 inline-flex h-14 w-full items-center justify-center gap-3 rounded-full bg-stone-900 px-8 text-sm font-semibold text-white transition-colors duration-300 hover:bg-orange-700 disabled:opacity-60 sm:w-auto">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
        {busy ? "Sending…" : { quote: "Send quote request", site_survey: "Request site survey", consultation: "Request consultation", contact: "Send enquiry" }[type]}
        {!busy && <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />}
      </button>
    </form>
  );
};
